import React, { useCallback, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  UploadInput,
  DropDownInput,
  AddressInputField,
  InputField,
  Button,
  DateInput,
} from "@/components";
import { IoArrowForward } from "react-icons/io5";
import { statesOptionsArray } from "../../../utils/Data.js";
import { personalDetailsSchema } from "../../../schemas";
import { useFieldError, useValidation } from "@/";
import { useSelector } from "react-redux";

const PersonalDetails = () => {
  const user = useSelector((state) => state.user?.user) || {};
  const userId = `User ID : ${user?.clientId}`;
  const joinDate = user?.createdAt;
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  const { handleNextStep, formData, setFormData, isDisabled } =
    useOutletContext();
  const { validate, errors } = useValidation(personalDetailsSchema);
  const [formErrors, setFormErrors] = useState(errors);
  const getErrorMessage = useFieldError(formErrors || []);

  // Options for Gender dropdown
  const GenderOption = {
    Male: "Male",
    Female: "Female",
    Other: "Other",
  };

  const genderOptionsArray = Object.keys(GenderOption).map((key) => ({
    label: GenderOption[key],
    value: key,
  }));

  // Options for monthly shipments dropdown
  const ShipmentsRange = {
    10: "10-100",
    100: "100-500",
    500: "500-1000",
    1000: "1000-5000",
    5000: "5000+",
  };

  const monthlyShipmentsOptionsArray = Object.keys(ShipmentsRange).map(
    (key) => ({
      label: ShipmentsRange[key],
      value: key.toString(),
    })
  );

  // Update top-level personalDetails fields inside data
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      personalDetails: {
        ...prev.personalDetails,
        [field]: value,
      },
    }));
  };

  // Update nested address fields in personalDetails.address
  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      personalDetails: {
        ...prev.personalDetails,
        address: {
          ...prev.personalDetails.address,
          [field]: value,
        },
      },
    }));
  };

  const handleNext = () => {
    if (isDisabled) {
      handleNextStep();
      return;
    }
    setFormErrors([]);
    const result = validate(formData.personalDetails);
    console.log("Personal data", result.data);

    if (!result.isValid) {
      console.log("Validation errors:", result.errors);
      setFormErrors(result.errors);
      return;
    }
    handleNextStep();
  };

  return (
    <div className="flex flex-col gap-4 md:gap-4 w-full p-2">
      <h2 className="md:text-headline3 text-headline4 font-nunito text-secondary-90 font-bold">
        Personal Details
      </h2>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-col gap-3 w-full"
      >
        {user && (
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-end">
            <InputField
              placeholder="User ID :  -----------"
              value={userId}
              disabled={true}
              className="w-full"
            />
            <InputField
              placeholder="Joining Date :  --/--/----"
              className="w-full"
              value={`Joining Date : ${formatDate(joinDate)}`}
              disabled={true}
            />
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Full Name"
            placeholder="Enter your name"
            type="text"
            value={formData?.personalDetails?.fullName || ""}
            onChange={(val) => handleChange("fullName", val)}
            error={getErrorMessage("fullName")}
            disabled={isDisabled}
          />
          <InputField
            label="Email ID"
            placeholder="Enter your email"
            type="email"
            value={formData?.personalDetails?.email || ""}
            onChange={(val) => handleChange("email", val)}
            error={getErrorMessage("email")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Phone No. 1"
            placeholder="Enter your phone number"
            type="number"
            maxLength={10}
            value={formData?.personalDetails?.phone1 || ""}
            onChange={(val) => handleChange("phone1", val)}
            error={getErrorMessage("phone1")}
            disabled={isDisabled}
          />
          <InputField
            label="Phone No. 2"
            placeholder="Enter your phone number"
            type="number"
            maxLength={10}
            value={formData?.personalDetails?.phone2 || ""}
            onChange={(val) => handleChange("phone2", val)}
            error={getErrorMessage("phone2")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <DateInput
            label="Date Of Birth"
            placeholder="Select Date"
            value={formData?.personalDetails?.dob || ""}
            onChange={(value) => handleChange("dob", value)}
            error={getErrorMessage("dob")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="Gender"
            placeholder="Select your gender"
            value={formData?.personalDetails?.gender || ""}
            onChange={(value) => handleChange("gender", value)}
            options={genderOptionsArray}
            error={getErrorMessage("gender")}
            disabled={isDisabled}
          />
          <InputField
            label="Pincode"
            placeholder="Enter your pincode"
            type="number"
            maxLength={6}
            value={formData?.personalDetails?.address?.pincode || ""}
            onChange={(val) => handleAddressChange("pincode", val)}
            error={getErrorMessage("address.pincode")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="City"
            placeholder="Enter your city"
            type="text"
            value={formData?.personalDetails?.address?.city || ""}
            onChange={(val) => handleAddressChange("city", val)}
            error={getErrorMessage("address.city")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="State"
            value={formData?.personalDetails?.address?.state || ""}
            onChange={(value) => handleAddressChange("state", value)}
            placeholder="Select your state"
            options={statesOptionsArray}
            error={getErrorMessage("address.state")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <AddressInputField
            label="Address"
            placeholder="Enter your address"
            type="text"
            value={formData?.personalDetails?.address?.line1 || ""}
            onChange={(val) => handleAddressChange("line1", val)}
            error={getErrorMessage("address.line1")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="Monthly Shipments"
            value={formData?.personalDetails?.monthlyShipments.toString() || ""}
            onChange={(value) => handleChange("monthlyShipments", value)}
            options={monthlyShipmentsOptionsArray}
            placeholder="Select monthly order volume"
            error={getErrorMessage("monthlyShipments")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Aadhar No."
            placeholder="Enter your aadhar no."
            maxLength={12}
            value={formData?.personalDetails?.aadharNumber || ""}
            onChange={(val) => handleChange("aadharNumber", val)}
            error={getErrorMessage("aadharNumber")}
            disabled={isDisabled}
          />
          <InputField
            label="Pan No."
            placeholder="Enter your pan no."
            maxLength={10}
            value={formData?.personalDetails?.panNumber || ""}
            onChange={(val) => handleChange("panNumber", val)}
            error={getErrorMessage("panNumber")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <UploadInput
            label="Aadhar Photo"
            placeholder="Upload Aadhar card photo."
            value={formData?.personalDetails?.aadhar || null}
            onChange={(val) => handleChange("aadhar", val)}
            note="Note: File type should only be png, jpeg and should be in color"
            noteClassName="text-caption md:text-body1 pr-2"
            spanClassName="text-caption md:text-body1"
            error={getErrorMessage("aadhar")}
            disabled={isDisabled}
          />
          <UploadInput
            label="PAN Photo."
            placeholder="Upload PAN card photo."
            value={formData?.personalDetails?.pan || null}
            onChange={(val) => handleChange("pan", val)}
            note="Note: File type should only be png, jpeg and should be in color"
            noteClassName="text-caption md:text-body1 pr-2"
            spanClassName="text-caption md:text-body1"
            error={getErrorMessage("pan")}
            disabled={isDisabled}
          />
        </div>
      </form>

      <div className="flex justify-end mt-8 mr-2">
        <Button
          size="sm"
          variant="default"
          onClick={handleNext}
          icon={IoArrowForward}
        >
          {isDisabled ? "Next" : "Save And Next"}
        </Button>
      </div>
    </div>
  );
};

export default PersonalDetails;
